import { useCallback, useEffect, useMemo, useState } from "react";
import { getAllBranch, getAllRoutesByCompany } from "../../services";
import { Branch } from "../../types/user.type";
import { SaleRoute } from "../../types/sale-route.type";

/** The สาขา / สายส่ง filters' ทั้งหมด option — the one value that is not a code. */
export const UNFILTERED_OPTION = "all";

interface BranchRouteOptions {
  /** Empty for a token-scoped user: they have no picker, and the list is never fetched. */
  branches: Branch[];
  /** สายส่ง narrowed to the picked branch, or every route for a token-scoped user. */
  routeOptions: SaleRoute[];
  /** True while a cross-branch user is still on ทั้งหมด — there is no branch to list yet. */
  isRouteDisabled: boolean;
}

/**
 * The สาขา and สายส่ง option lists for the รายงานการโอน filter rows.
 *
 * Shared by the route-level report and the คำขอแก้ไขยอดโอน queue, which offer the same two
 * pickers and must agree on which routes belong to a branch: the route list comes back for
 * the whole company, so a cross-branch user's สายส่ง options are the routes whose
 * `branch_uuid` matches the branch they picked — which is why the branch list is needed for
 * more than filling the picker.
 *
 * A token-scoped user has no branch picker at all, and needs no branch list either: the API
 * already answers with their own branch's routes, so every route it returns is theirs.
 */
export function useBranchRouteOptions(
  canSelectBranch: boolean,
  selectedBranch: string,
): BranchRouteOptions {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [routes, setRoutes] = useState<SaleRoute[]>([]);

  const onGetAllBranch = useCallback(async () => {
    const {
      data: { data },
    } = await getAllBranch();
    setBranches(data ?? []);
  }, []);

  const onGetAllRoutes = useCallback(async () => {
    const {
      data: { data },
    } = await getAllRoutesByCompany({ includeSpecial: true });
    setRoutes(data ?? []);
  }, []);

  useEffect(() => {
    if (canSelectBranch) onGetAllBranch();
    onGetAllRoutes();
  }, [canSelectBranch, onGetAllBranch, onGetAllRoutes]);

  const routeOptions = useMemo(() => {
    if (!canSelectBranch) return routes;
    if (selectedBranch === UNFILTERED_OPTION) return [];
    const branchUuid = branches.find(
      (branch) => branch.branch_code === selectedBranch,
    )?.uuid;
    return routes.filter((route) => route.branch_uuid === branchUuid);
  }, [canSelectBranch, routes, branches, selectedBranch]);

  return {
    branches,
    routeOptions,
    isRouteDisabled: canSelectBranch && selectedBranch === UNFILTERED_OPTION,
  };
}
